import React, {useState, useRef, useEffect} from 'react';
import {
  GoogleMap,
  withScriptjs,
  withGoogleMap,
  TrafficLayer,
  Marker,
  InfoWindow,
  OverlayView,
} from 'react-google-maps';

// marker to display the site office location and an info window on click
const SiteOfficeMarker = ({siteOfficeLocation}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Marker
      icon={{
        url: '/assets/images/officeMarker.png',
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 20),
        scale: 0.7,
        rotation: 90,
      }}
      position={{
        lat: parseFloat(siteOfficeLocation.location.latitude),
        lng: parseFloat(siteOfficeLocation.location.longitude),
      }}
      onClick={handleToggle}
    >
      {isOpen && (
        <InfoWindow
          position={{
            lat: parseFloat(siteOfficeLocation.location.latitude),
            lng: parseFloat(siteOfficeLocation.location.longitude),
          }}
          onCloseClick={handleToggle}
        >
          <div style={{color: 'black'}} className='point-details deboard'>
            <span className='img-s'>
              <img
                className='user-image'
                src={localStorage.getItem('COMPANY_LOGO')}
              />
            </span>
            <span className='user-info'>
              <h4>{siteOfficeLocation?.officeName}</h4>
              <p>{siteOfficeLocation?.location?.locName}</p>
            </span>
          </div>
        </InfoWindow>
      )}
    </Marker>
  );
};

// Overlay-based vehicle marker with rotation by heading and icon based on trip/gender
const VehicleOverlayMarker = ({vehicle, isOnline = false}) => {
  const deg = Number(vehicle.heading || 0);
  const size = 40; // px
  const half = size / 2;

  const meta = vehicle?.metadata || {};
  const info = meta?.tripInfo || {};
  const flags = meta?.tripFlags || {};

  // Determine trip status
  const rawStatus = String(vehicle?.tripStatus || info?.tripStatus || '').toUpperCase();
  const isCompleted = rawStatus === 'COMPLETED';

  // Determine passenger list and exclude escort when present
  const onBoard = meta?.passengersInfo?.onBoardPassengers || [];
  const deBoard = meta?.passengersInfo?.deBoardPassengers || [];
  const preferredPassengers = onBoard.length ? onBoard : deBoard;

  const normalizeName = (s) => (s ? String(s).toLowerCase().replace(/\s+/g, ' ').trim() : '');
  const escortsFromPassengers = preferredPassengers.filter(p => String(p?.passType || '').toUpperCase() === 'ESCORT');
  const escortNameFromPassengers = escortsFromPassengers[0]?.name || '';
  const escortNameRaw = (info?.escortName || meta?.escortName || meta?.tripEscortName || meta?.escortFullName ||
    (meta?.escortInfo && (meta?.escortInfo?.name || meta?.escortInfo?.escortName)) || vehicle?.escortName || '')
    .toString();
  const escortNameToMatch = escortNameRaw || escortNameFromPassengers || '';
  const normalizedEscortName = normalizeName(escortNameToMatch);

  const nonEscortPassengers = preferredPassengers.filter((p) => {
    const passTypeEscort = String(p?.passType || '').toUpperCase() === 'ESCORT';
    const nameMatchesEscort = normalizedEscortName && normalizeName(p?.name) === normalizedEscortName;
    return !(passTypeEscort || nameMatchesEscort);
  });

  // Female detection, fallback to flags if list empty
  const hasFemaleInList = nonEscortPassengers.some(p => String(p?.gender || '').trim().toLowerCase().startsWith('f'));
  const femalePresent = nonEscortPassengers.length > 0 ? hasFemaleInList : (flags?.isFemaleTraveling === true || flags?.isFemaleTraveling1 === true);


  // Icon priority:
  // - Online marker (inactive) always wins
  // - Completed trips should show the green/available car during the grace period
  // - Otherwise, use female/male styling
  let iconSrc = '/assets/images/male_car.svg';
  if (isOnline) {
    iconSrc = '/assets/images/inactive_car.svg';
  } else if (isCompleted) {
    iconSrc = '/assets/images/available_car.svg';
  } else if (femalePresent) {
    iconSrc = '/assets/images/female_car.svg';
  }

  return (
    <OverlayView
      position={{ lat: vehicle?.latitude, lng: vehicle?.longitude }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div
        title={`${vehicle?.metadata?.vehicleInfo?.vehicleNo} (${vehicle?.metadata?.driverInfo?.driverName})`}
        style={{
          position: 'absolute',
          left: -half,
          top: -half,
          width: size,
          height: size,
          transform: `rotate(${deg}deg)`,
          transformOrigin: 'center center',
          willChange: 'transform',
          pointerEvents: 'auto',
        }}
      >
        <img
          src={iconSrc}
          alt={isOnline ? vehicle?.driverId : vehicle?.tripId}
          style={{ width: '100%', height: '100%', display: 'block' }}
        />
      </div>
    </OverlayView>
  );
};

// main map component that displays the site office and vehicle markers
const Map = ({center, zoom, siteOfficeLocation, vehicles, vehiclesOnline, fitAllVehicles, onFitDone}) => {
  const mapRef = useRef(null);
  const hasInitialFitRef = useRef(false);

  useEffect(() => {
    if (mapRef.current && center && typeof center.lat === 'number' && typeof center.lng === 'number') {
      mapRef.current.panTo(center);
    }
  }, [center]);

  // Initial fit to show all site offices when they first load
  useEffect(() => {
    if (hasInitialFitRef.current) return;
    const offices = Array.isArray(siteOfficeLocation) ? siteOfficeLocation : [];
    if (offices.length === 0) return;
    const map = mapRef.current;
    if (!map) return;

    const validOffices = offices.filter(o => o?.location?.latitude && o?.location?.longitude);
    if (validOffices.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    validOffices.forEach(o => bounds.extend(new window.google.maps.LatLng(
      parseFloat(o.location.latitude),
      parseFloat(o.location.longitude)
    )));

    if (validOffices.length === 1) {
      // Pad bounds slightly so fitBounds approximates zoom ~15 for a single office
      const center = bounds.getCenter();
      const offset = 0.005;
      bounds.extend(new window.google.maps.LatLng(center.lat() + offset, center.lng() + offset));
      bounds.extend(new window.google.maps.LatLng(center.lat() - offset, center.lng() - offset));
    }

    map.fitBounds(bounds);
    hasInitialFitRef.current = true;
  }, [siteOfficeLocation]);

  // One-shot fit of all vehicles (trip + online) when requested
  useEffect(() => {
    if (!fitAllVehicles) return;
    const map = mapRef.current;
    if (!map) return;
    const tripList = Object.values(vehicles || {});
    const onlineList = Object.values(vehiclesOnline || {});
    const vehicleList = [...tripList, ...onlineList];
    if (vehicleList.length < 2) {
      if (onFitDone) onFitDone();
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    vehicleList.forEach(v => bounds.extend(new window.google.maps.LatLng(v.latitude, v.longitude)));
    map.fitBounds(bounds);
    if (onFitDone) onFitDone();
  }, [fitAllVehicles, vehicles, vehiclesOnline, onFitDone]);


  return (
    <GoogleMap 
      ref={mapRef} 
      defaultZoom={zoom} 
      defaultCenter={center}
      defaultOptions={{
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
        styles: [{}] // empty array to reset google logo colors
      }}
    >
      <TrafficLayer />
      {(Array.isArray(siteOfficeLocation) ? siteOfficeLocation : []).map((office, index) => (
        office?.location?.latitude && (
          <SiteOfficeMarker key={`site-office-${index}`} siteOfficeLocation={office} />
        )
      ))}
      {Object.values(vehicles)
        .filter((v) => typeof v?.latitude === 'number' && typeof v?.longitude === 'number')
        .map((vehicle) => (
          <VehicleOverlayMarker key={vehicle.tripId} vehicle={vehicle} isOnline={false} />
        ))}
      {Object.values(vehiclesOnline)
        .filter((v) => typeof v?.latitude === 'number' && typeof v?.longitude === 'number')
        .map((vehicle) => (
          <VehicleOverlayMarker key={vehicle.driverId} vehicle={vehicle} isOnline={true} />
        ))}
    </GoogleMap>
  );
};

const WrappedMap = withScriptjs(withGoogleMap(Map));

export default WrappedMap;
